/**
 * Formatos dos dados trocados entre o site e a API no Módulo 1.
 * Os mesmos schemas validam o formulário (feedback imediato) e a API (proteção real).
 */
import { z } from 'zod';
import { PERFIS } from '../dominio/perfis';
import { novaSenhaSchema } from './senha';

const email = z.email({ message: 'Informe um e-mail válido.' }).trim().toLowerCase().max(255);

const texto = (rotulo: string, max = 255) =>
  z
    .string()
    .trim()
    .min(1, { message: `Informe ${rotulo}.` })
    .max(max, { message: `${rotulo} deve ter no máximo ${max} caracteres.` });

const opcional = (max = 255) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const codigoIbgeSchema = z
  .string()
  .regex(/^\d{7}$/, { message: 'Código IBGE deve ter 7 dígitos.' });

// ---------------------------------------------------------------- conta
export const entrarSchema = z.object({
  email,
  senha: z.string().min(1, { message: 'Informe a senha.' }).max(256),
});
export type DadosEntrar = z.infer<typeof entrarSchema>;

export const trocarSenhaSchema = z
  .object({
    senhaAtual: z.string().min(1, { message: 'Informe a senha atual.' }).max(256),
    novaSenha: novaSenhaSchema,
    confirmacao: z.string(),
  })
  .refine((d) => d.novaSenha === d.confirmacao, {
    path: ['confirmacao'],
    message: 'A confirmação não é igual à nova senha.',
  })
  .refine((d) => d.novaSenha !== d.senhaAtual, {
    path: ['novaSenha'],
    message: 'A nova senha deve ser diferente da atual.',
  });
export type DadosTrocarSenha = z.infer<typeof trocarSenhaSchema>;

export const esqueciSenhaSchema = z.object({ email });

export const redefinirSenhaSchema = z
  .object({
    token: z.string().min(20).max(200),
    novaSenha: novaSenhaSchema,
    confirmacao: z.string(),
  })
  .refine((d) => d.novaSenha === d.confirmacao, {
    path: ['confirmacao'],
    message: 'A confirmação não é igual à nova senha.',
  });
export type DadosRedefinirSenha = z.infer<typeof redefinirSenhaSchema>;

export const solicitarAjudaSchema = z.object({
  nome: texto('seu nome'),
  contato: texto('um telefone ou e-mail para retorno'),
  unidade: opcional(),
  mensagem: opcional(1000),
});
export type DadosSolicitarAjuda = z.infer<typeof solicitarAjudaSchema>;

// ---------------------------------------------------------------- administração
export const usuarioSchema = z.object({
  nome: texto('o nome'),
  email,
  perfil: z.enum(PERFIS, { message: 'Escolha um perfil.' }),
  municipioIbge: codigoIbgeSchema.nullable(),
  unidadeSaudeId: z.uuid().nullable(),
  registroProfissional: opcional(50),
  tipoRegistro: opcional(30),
  funcao: opcional(120),
  telefone: opcional(30),
  celular: opcional(30),
});
export type DadosUsuario = z.infer<typeof usuarioSchema>;

export const TIPOS_UNIDADE = [
  'ubs',
  'hospital',
  'maternidade',
  'ambulatorio',
  'casa_gestante',
  'secretaria_saude',
  'outro',
] as const;

export const unidadeSchema = z.object({
  nome: texto('o nome da unidade'),
  tipoUnidade: z.enum(TIPOS_UNIDADE),
  cnes: z
    .string()
    .trim()
    .regex(/^\d{7}$/, { message: 'CNES deve ter 7 dígitos.' })
    .nullable()
    .or(z.literal('').transform(() => null)),
  municipioIbge: codigoIbgeSchema,
  telefone: opcional(30),
  email: z
    .email()
    .nullable()
    .or(z.literal('').transform(() => null)),
});
export type DadosUnidade = z.infer<typeof unidadeSchema>;

export const municipioSchema = z.object({
  codigoIbge: codigoIbgeSchema,
  nome: texto('o nome do município', 120),
  uf: z.string().regex(/^[A-Z]{2}$/, { message: 'UF com 2 letras maiúsculas.' }),
  participante: z.boolean(),
  dataAdesao: z.iso.date().nullable(),
});
export type DadosMunicipio = z.infer<typeof municipioSchema>;

export const agravosDisponiveisSchema = z.object({
  sifilis: z.boolean(),
  hiv: z.boolean(),
  hbv: z.boolean(),
  hcv: z.boolean(),
  chagas: z.boolean(),
});
export type AgravosDisponiveis = z.infer<typeof agravosDisponiveisSchema>;
